import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Chip, Button, Alert,
    CircularProgress, TextField, Divider, Stack, Tooltip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    CloudQueue as RailwayIcon,
    Cloud as RenderIcon,
    Computer as LocalIcon,
    CheckCircle as OkIcon,
    Cancel as FailIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    SwapHoriz as SwitchIcon,
    FiberManualRecord as LiveDot,
    NetworkCheck as PingIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const PLATFORMS = ['render', 'railway'];

const PLATFORM_META = {
    render: {
        label: 'Render',
        Icon: RenderIcon,
        color: '#46e3b7',
        docsUrl: 'https://render.com/docs',
    },
    railway: {
        label: 'Railway',
        Icon: RailwayIcon,
        color: '#7c3aed',
        docsUrl: 'https://docs.railway.app',
    },
    local: {
        label: 'Local Dev',
        Icon: LocalIcon,
        color: '#888',
        docsUrl: null,
    },
};

const ENV_NAMES = ['production', 'staging'];

const URL_FIELDS = [
    { key: 'backend_url',  label: 'Backend URL',  placeholder: 'https://api.socialixer.com' },
    { key: 'frontend_url', label: 'Frontend URL', placeholder: 'https://socialixer.com' },
    { key: 'notes',        label: 'Notes',        placeholder: 'Any notes for this environment…' },
];

// ─── PlatformCard ─────────────────────────────────────────────────────────────

function PlatformCard({ platform, isActive, isLive, onClick, pingResults, onPing, envData }) {
    const theme  = useTheme();
    const { label, Icon, color } = PLATFORM_META[platform];

    const prodPing = pingResults?.[platform]?.production;
    const stagPing = pingResults?.[platform]?.staging;

    return (
        <Card
            sx={{
                borderRadius: '16px',
                border: `2px solid ${isActive ? alpha(color, 0.6) : alpha(theme.palette.divider, 0.15)}`,
                bgcolor: isActive ? alpha(color, 0.06) : alpha(theme.palette.background.paper, 0.5),
                backdropFilter: 'blur(16px)',
                transition: 'all 0.2s',
                cursor: isActive ? 'default' : 'pointer',
                '&:hover': { border: `2px solid ${alpha(color, 0.5)}` },
            }}
            onClick={isActive ? undefined : onClick}
        >
            <CardContent sx={{ p: 3 }}>
                {/* Header row */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '12px',
                        bgcolor: alpha(color, 0.15),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Icon sx={{ color, fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{label}</Typography>
                        {isLive && (
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                                <LiveDot sx={{ fontSize: 10, color: 'success.main' }} />
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>LIVE SERVER</Typography>
                            </Stack>
                        )}
                    </Box>
                    {isActive ? (
                        <Chip label="Active" size="small" sx={{ fontWeight: 800, bgcolor: alpha(color, 0.15), color }} />
                    ) : (
                        <Chip label="Inactive" size="small" variant="outlined" sx={{ fontWeight: 700, color: 'text.disabled' }} />
                    )}
                </Stack>

                {/* Environment ping rows */}
                {ENV_NAMES.map(env => {
                    const backendUrl = envData?.[platform]?.[env]?.backend_url;
                    const ping = env === 'production' ? prodPing : stagPing;
                    return (
                        <Stack key={env} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                            <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary', width: 80, flexShrink: 0 }}>
                                {env}
                            </Typography>
                            {backendUrl ? (
                                <>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {backendUrl}
                                    </Typography>
                                    {ping ? (
                                        <Tooltip title={ping.error || `${ping.latency_ms}ms`} arrow>
                                            <Chip
                                                icon={ping.ok ? <OkIcon sx={{ fontSize: '12px !important' }} /> : <FailIcon sx={{ fontSize: '12px !important' }} />}
                                                label={ping.ok ? `${ping.latency_ms}ms` : (ping.error || `${ping.status_code}`)}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.68rem',
                                                    fontWeight: 700,
                                                    bgcolor: ping.ok ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                                                    color: ping.ok ? 'success.main' : 'error.main',
                                                }}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title={`Ping ${env} backend`} arrow>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onPing(platform, env, backendUrl.replace(/\/$/, '') + '/healthz'); }}>
                                                <PingIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            ) : (
                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>URL not configured</Typography>
                            )}
                        </Stack>
                    );
                })}
            </CardContent>
        </Card>
    );
}

// ─── EnvEditor ────────────────────────────────────────────────────────────────

function EnvEditor({ platform, envName, values, onChange }) {
    const { color } = PLATFORM_META[platform];
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: alpha(color, 0.9), letterSpacing: '0.1em' }}>
                {platform} / {envName}
            </Typography>
            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {URL_FIELDS.map(f => (
                        <TextField
                        key={f.key}
                        label={f.label}
                        placeholder={f.placeholder}
                        value={values?.[f.key] || ''}
                        onChange={e => onChange(platform, envName, f.key, e.target.value)}
                        size="small"
                        sx={{ gridColumn: f.key === 'notes' ? 'span 2' : undefined }}
                        inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
                    />
                ))}
            </Box>
            <Divider sx={{ mt: 2, opacity: 0.3 }} />
        </Box>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const AdminInfrastructure = () => {
    const theme = useTheme();

    // Core state
    const [loading,  setLoading]  = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [error,    setError]    = useState(null);
    const [success,  setSuccess]  = useState(null);

    // Config from Firestore
    const [livePlatform,    setLivePlatform]    = useState(null);  // actual running env
    const [activePlatform,  setActivePlatform]  = useState(null);  // saved preference
    const [environments,    setEnvironments]    = useState({});    // URL configs

    // Ping results: { render: { production: {...}, staging: {...} }, railway: {...} }
    const [pingResults, setPingResults] = useState({});

    // Confirm switch dialog
    const [switchDialog, setSwitchDialog] = useState(null); // { platform }

    // Edit mode
    const [editMode, setEditMode] = useState(false);

    // ── Fetch ──────────────────────────────────────────────────────────────────

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { live_platform, config } = await adminApi.getInfraConfig();
            setLivePlatform(live_platform);
            setActivePlatform(config?.active_platform || null);
            setEnvironments(config?.environments || {});
        } catch (err) {
            setError(err.message || 'Failed to load infra config');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    // ── Ping ───────────────────────────────────────────────────────────────────

    const handlePing = useCallback(async (platform, env, url) => {
        try {
            const result = await adminApi.pingInfraUrl(url);
            setPingResults(prev => ({
                ...prev,
                [platform]: { ...(prev[platform] || {}), [env]: result },
            }));
        } catch {
            setPingResults(prev => ({
                ...prev,
                [platform]: { ...(prev[platform] || {}), [env]: { ok: false, error: 'request failed' } },
            }));
        }
    }, []);

    const pingAll = useCallback(() => {
        PLATFORMS.forEach(platform => {
            ENV_NAMES.forEach(env => {
                const url = environments?.[platform]?.[env]?.backend_url;
                if (url) handlePing(platform, env, url.replace(/\/$/, '') + '/healthz');
            });
        });
    }, [environments, handlePing]);

    // ── Edit environment URLs ──────────────────────────────────────────────────

    const handleEnvChange = (platform, env, field, value) => {
        setEnvironments(prev => ({
            ...prev,
            [platform]: {
                ...(prev[platform] || {}),
                [env]: {
                    ...(prev[platform]?.[env] || {}),
                    [field]: value,
                },
            },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const data = await adminApi.updateInfraConfig({ environments });
            setSuccess('Environment URLs saved successfully');
            setEditMode(false);
            if (data.warning) setError(`⚠️ ${data.warning}`);
        } catch (err) {
            setError(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    // ── Switch platform ────────────────────────────────────────────────────────

    const confirmSwitch = (platform) => setSwitchDialog({ platform });
    const cancelSwitch  = () => setSwitchDialog(null);

    const handleSwitch = async () => {
        if (!switchDialog) return;
        const { platform } = switchDialog;
        cancelSwitch();
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const data = await adminApi.switchPlatform(platform);
            setActivePlatform(platform);
            setSuccess(`Active platform switched to ${PLATFORM_META[platform].label}`);
            if (data.warning) setError(`⚠️ ${data.warning}`);
        } catch (err) {
            setError(err.message || 'Switch failed');
        } finally {
            setSaving(false);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>Infrastructure</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Switch the active hosting platform between Render and Railway.
                        The "Live Server" badge reflects where this backend is actually running.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={() => { fetchConfig(); setPingResults({}); }}
                        disabled={loading || saving}
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: '10px' }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PingIcon />}
                        onClick={pingAll}
                        disabled={loading || saving}
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: '10px' }}
                    >
                        Ping All
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setEditMode(v => !v)}
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: '10px' }}
                    >
                        {editMode ? 'Cancel Edit' : 'Edit URLs'}
                    </Button>
                </Stack>
            </Box>

            {error   && <Alert severity="error"   sx={{ mb: 3, borderRadius: '10px' }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {/* Live banner */}
            {livePlatform && (
                <Alert
                    severity="info"
                    icon={<LiveDot sx={{ fontSize: 14, mt: 0.5 }} />}
                    sx={{ mb: 3, borderRadius: '10px', fontWeight: 700 }}
                >
                    This admin page is currently talking to&nbsp;
                    <strong>{PLATFORM_META[livePlatform]?.label ?? livePlatform}</strong>.
                    {activePlatform && (
                        <>&nbsp;Frontends route API calls to <strong>{PLATFORM_META[activePlatform]?.label ?? activePlatform}</strong>{' '}
                        (<code>{environments?.[activePlatform]?.production?.backend_url || 'no URL set'}</code>) on next page load.</>
                    )}
                </Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && (
                <>
                    {/* Platform cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {PLATFORMS.map(platform => (
                            <Grid item xs={12} md={6} key={platform}>
                                <PlatformCard
                                    platform={platform}
                                    isActive={activePlatform === platform}
                                    isLive={livePlatform === platform}
                                    onClick={() => confirmSwitch(platform)}
                                    pingResults={pingResults}
                                    onPing={handlePing}
                                    envData={environments}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* URL editor */}
                    {editMode && (
                        <Card sx={{
                            borderRadius: '16px',
                            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                            bgcolor: alpha(theme.palette.background.paper, 0.5),
                            backdropFilter: 'blur(16px)',
                            mb: 3,
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>
                                    Environment URLs
                                </Typography>
                                {PLATFORMS.map(platform =>
                                    ENV_NAMES.map(env => (
                                        <EnvEditor
                                            key={`${platform}-${env}`}
                                            platform={platform}
                                            envName={env}
                                            values={environments?.[platform]?.[env]}
                                            onChange={handleEnvChange}
                                        />
                                    ))
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                        onClick={handleSave}
                                        disabled={saving}
                                        sx={{ fontWeight: 700, borderRadius: '10px' }}
                                    >
                                        {saving ? 'Saving…' : 'Save URLs'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Switch confirm dialog */}
            <Dialog
                open={Boolean(switchDialog)}
                onClose={cancelSwitch}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>
                    Switch active backend to {switchDialog ? PLATFORM_META[switchDialog.platform]?.label : ''}?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        Updates <code>settings/public_infra.active_backend_url</code> in Firestore to{' '}
                        <strong>{switchDialog ? environments?.[switchDialog.platform]?.production?.backend_url || '(not configured)' : ''}</strong>.
                    </DialogContentText>
                    <Alert severity="info" sx={{ mt: 2, borderRadius: '8px', fontSize: '0.82rem' }}>
                        On their next page load, all users' frontends will read the new URL and route API calls
                        to <strong>{switchDialog ? PLATFORM_META[switchDialog.platform]?.label : ''}</strong>.
                        Currently open tabs continue using the old URL until they reload. No redeploy needed.
                    </Alert>
                    {switchDialog && !environments?.[switchDialog.platform]?.production?.backend_url && (
                        <Alert severity="warning" sx={{ mt: 2, borderRadius: '8px', fontSize: '0.82rem' }}>
                            No <code>backend_url</code> is set for {PLATFORM_META[switchDialog.platform]?.label} / production.
                            Click <strong>Edit URLs</strong> first to configure it, then come back to switch.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={cancelSwitch} sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button
                        onClick={handleSwitch}
                        variant="contained"
                        sx={{ fontWeight: 700, borderRadius: '8px' }}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SwitchIcon />}
                        disabled={saving || !switchDialog || !environments?.[switchDialog.platform]?.production?.backend_url}
                    >
                        Switch
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminInfrastructure;
